import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatBrl } from '../../../utils/currency.js';
import { productIsSoldOut, productTrackedStock } from '../../../utils/productStock.js';
import { parseStoreDocumentOrderFile, parseStoreDocumentOrderWithAi } from './documentOrderApi.js';

/**
 * @typedef {object} DocOrderRow
 * @property {number} line
 * @property {{ codigo?: string | null, nome: string, quantidade: number, preco_unitario?: number | null }} document
 * @property {number} quantity
 * @property {{
 *   status: string,
 *   product_id?: number | null,
 *   product_nome?: string | null,
 *   preco?: number | null,
 *   estoque?: number | null,
 *   candidates?: Array<{ id: number, nome: string, score?: number }>,
 * }} match
 */

/**
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   slug: string,
 *   aiAvailable: boolean,
 *   blockSaleZeroStock: boolean,
 *   pricingHidden: boolean,
 *   resolveProductRow: (id: number) => Record<string, unknown> | null,
 *   addQty: (id: number, delta: number) => void,
 *   onRequireLogin?: () => void,
 *   mustAuth?: boolean,
 *   customerSession?: unknown,
 * }} props
 */
export function DocumentOrderModal({
  open,
  onClose,
  slug,
  aiAvailable,
  blockSaleZeroStock,
  pricingHidden,
  resolveProductRow,
  addQty,
  onRequireLogin,
  mustAuth = false,
  customerSession = null,
}) {
  const [phase, setPhase] = useState('pick');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [canTryAi, setCanTryAi] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [rows, setRows] = useState(/** @type {DocOrderRow[]} */ ([]));
  const [method, setMethod] = useState('');
  const fileRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const lastFileRef = useRef(/** @type {File | null} */ (null));

  const reset = useCallback(() => {
    setPhase('pick');
    setBusy(false);
    setError('');
    setCanTryAi(false);
    setSessionId('');
    setRows([]);
    setMethod('');
    lastFileRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const closeModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const applyParseResponse = useCallback((res) => {
    if (res?.ok && Array.isArray(res.items)) {
      setRows(
        res.items.map((row) => ({
          ...row,
          quantity: Number(row.quantity) > 0 ? Number(row.quantity) : Number(row.document?.quantidade) || 1,
        })),
      );
      setSessionId(typeof res.session_id === 'string' ? res.session_id : '');
      setMethod(typeof res.method === 'string' ? res.method : '');
      setPhase('review');
      setError('');
      setCanTryAi(false);
      return;
    }
    setError(typeof res?.error === 'string' ? res.error : 'Não foi possível ler o documento.');
    setCanTryAi(Boolean(res?.can_try_ai) && aiAvailable);
    if (typeof res?.session_id === 'string') setSessionId(res.session_id);
    setPhase('error');
  }, [aiAvailable]);

  const onFilePicked = useCallback(
    async (file) => {
      if (!file || busy) return;
      if (mustAuth && !customerSession) {
        onRequireLogin?.();
        return;
      }
      lastFileRef.current = file;
      setBusy(true);
      setPhase('loading');
      setError('');
      try {
        const res = await parseStoreDocumentOrderFile(slug, file);
        applyParseResponse(res);
      } catch (err) {
        const payload = err?.data;
        if (payload && typeof payload === 'object') {
          if (payload.error && !payload.ok) {
            const detail = typeof payload.detail === 'string' ? payload.detail.trim() : '';
            setError(detail !== '' ? `${payload.error} (${detail})` : String(payload.error));
            setCanTryAi(Boolean(payload.can_try_ai) && aiAvailable);
            if (typeof payload.session_id === 'string') setSessionId(payload.session_id);
            setPhase('error');
          } else {
            applyParseResponse(payload);
          }
        } else {
          setError(err?.message || 'Não foi possível enviar o arquivo.');
          setPhase('error');
        }
      } finally {
        setBusy(false);
      }
    },
    [applyParseResponse, busy, customerSession, mustAuth, onRequireLogin, slug],
  );

  const tryWithAi = useCallback(async () => {
    if (!aiAvailable || busy) return;
    setBusy(true);
    setPhase('loading');
    setError('');
    try {
      const res = await parseStoreDocumentOrderWithAi(slug, {
        sessionId: sessionId || undefined,
        file: lastFileRef.current,
      });
      applyParseResponse(res);
    } catch (err) {
      const payload = err?.data;
      if (payload && typeof payload === 'object' && payload.error) {
        setError(String(payload.error));
      } else {
        setError(err?.message || 'A análise com IA não funcionou desta vez.');
      }
      setPhase('error');
      setCanTryAi(false);
    } finally {
      setBusy(false);
    }
  }, [aiAvailable, applyParseResponse, busy, sessionId, slug]);

  const updateRowQty = useCallback((line, nextQty) => {
    const qty = Math.max(0, Math.floor(Number(nextQty) || 0));
    setRows((prev) =>
      prev.map((r) => (r.line === line ? { ...r, quantity: qty } : r)),
    );
  }, []);

  const pickCandidate = useCallback((line, productId) => {
    const pid = Number(productId);
    if (!Number.isFinite(pid) || pid <= 0) return;
    setRows((prev) =>
      prev.map((r) => {
        if (r.line !== line) return r;
        const cand = (r.match.candidates || []).find((c) => Number(c.id) === pid);
        return {
          ...r,
          match: {
            ...r.match,
            status: 'matched',
            product_id: pid,
            product_nome: cand?.nome || r.match.product_nome,
          },
        };
      }),
    );
  }, []);

  const readyRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          r.match.status === 'matched' &&
          Number(r.match.product_id) > 0 &&
          r.quantity > 0,
      ),
    [rows],
  );

  const blockedRows = useMemo(() => {
    if (!blockSaleZeroStock) return [];
    return readyRows.filter((r) => {
      const p = resolveProductRow(Number(r.match.product_id));
      return productIsSoldOut(p);
    });
  }, [blockSaleZeroStock, readyRows, resolveProductRow]);

  const addToCart = useCallback(() => {
    if (mustAuth && !customerSession) {
      onRequireLogin?.();
      return;
    }
    for (const r of readyRows) {
      const pid = Number(r.match.product_id);
      const p = resolveProductRow(pid);
      const tracked = productTrackedStock(p);
      let qty = r.quantity;
      if (tracked !== null && qty > tracked) qty = tracked;
      if (qty <= 0) continue;
      addQty(pid, qty);
    }
    closeModal();
  }, [addQty, closeModal, customerSession, mustAuth, onRequireLogin, readyRows, resolveProductRow]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="doc-order-title"
    >
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl dark:bg-zinc-900">
        <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
          <h2 id="doc-order-title" className="font-display text-base font-semibold text-zinc-900 dark:text-zinc-50">
            Pedir por nota ou lista
          </h2>
          <button
            type="button"
            onClick={closeModal}
            className="rounded-lg px-2 py-1 text-sm text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Fechar
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 text-sm text-zinc-700 dark:text-zinc-200">
          {phase === 'pick' ? (
            <div className="space-y-3">
              <p className="text-zinc-600 dark:text-zinc-300">
                Envie o <strong>XML da NF-e</strong> (recomendado), um <strong>CSV</strong> com código e quantidade,
                PDF da DANFE ou foto nítida do documento.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".xml,.csv,.pdf,image/*,text/plain"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFilePicked(f);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                className="w-full rounded-xl bg-[var(--st-600)] px-4 py-3 font-semibold text-white hover:opacity-95 disabled:opacity-50"
              >
                Escolher arquivo
              </button>
            </div>
          ) : null}

          {phase === 'loading' ? (
            <p className="py-8 text-center text-zinc-500 dark:text-zinc-400">Lendo documento…</p>
          ) : null}

          {phase === 'error' ? (
            <div className="space-y-4">
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-amber-950 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100">
                {error}
              </p>
              {canTryAi ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void tryWithAi()}
                  className="w-full rounded-xl border border-[var(--st-500)] px-4 py-2.5 font-semibold text-[var(--st-700)] hover:bg-[rgb(var(--st-rgb)/0.08)] dark:text-[var(--st-400)]"
                >
                  Tentar com IA
                </button>
              ) : null}
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setPhase('pick');
                  setError('');
                }}
                className="w-full rounded-xl bg-zinc-100 px-4 py-2.5 font-semibold text-zinc-800 dark:bg-zinc-800 dark:text-zinc-100"
              >
                Enviar outro arquivo
              </button>
            </div>
          ) : null}

          {phase === 'review' ? (
            <div className="space-y-3">
              {method ? (
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Lido via {method === 'nfe_xml' ? 'XML NF-e' : method === 'csv' ? 'CSV' : method === 'danfe_pdf' ? 'PDF' : method.startsWith('ai_') ? 'IA' : 'documento'}.
                </p>
              ) : null}
              <ul className="space-y-3">
                {rows.map((r) => {
                  const matched = r.match.status === 'matched' && Number(r.match.product_id) > 0;
                  const p = matched ? resolveProductRow(Number(r.match.product_id)) : null;
                  const soldOut = blockSaleZeroStock && matched && productIsSoldOut(p);
                  return (
                    <li
                      key={r.line}
                      className="rounded-xl border border-zinc-200 p-3 dark:border-zinc-700"
                    >
                      <p className="font-medium text-zinc-900 dark:text-zinc-50">{r.document.nome}</p>
                      {r.document.codigo ? (
                        <p className="text-xs text-zinc-500">Cód. documento: {r.document.codigo}</p>
                      ) : null}
                      {matched ? (
                        <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                          → {r.match.product_nome}
                          {!pricingHidden && r.match.preco != null ? ` · ${formatBrl(r.match.preco)}` : ''}
                        </p>
                      ) : r.match.status === 'ambiguous' ? (
                        <div className="mt-2">
                          <label className="text-xs font-semibold text-zinc-600 dark:text-zinc-300">
                            Escolha o produto
                            <select
                              className="mt-1 w-full rounded-lg border border-zinc-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-800"
                              defaultValue=""
                              onChange={(e) => pickCandidate(r.line, e.target.value)}
                            >
                              <option value="" disabled>
                                Selecione…
                              </option>
                              {(r.match.candidates || []).map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.nome}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      ) : (
                        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Produto não encontrado no catálogo</p>
                      )}
                      {soldOut ? (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">Sem estoque</p>
                      ) : matched ? (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            className="h-8 w-8 rounded-lg border border-zinc-300 dark:border-zinc-600"
                            onClick={() => updateRowQty(r.line, r.quantity - 1)}
                          >
                            −
                          </button>
                          <span className="min-w-[2rem] text-center font-semibold">{r.quantity}</span>
                          <button
                            type="button"
                            className="h-8 w-8 rounded-lg border border-zinc-300 dark:border-zinc-600"
                            onClick={() => updateRowQty(r.line, r.quantity + 1)}
                          >
                            +
                          </button>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}
        </div>

        {phase === 'review' ? (
          <div className="border-t border-zinc-200 px-4 py-3 dark:border-zinc-700">
            {blockedRows.length > 0 ? (
              <p className="mb-2 text-xs text-amber-700 dark:text-amber-300">
                Alguns itens estão sem estoque e não serão adicionados.
              </p>
            ) : null}
            <button
              type="button"
              disabled={readyRows.length === 0 || busy}
              onClick={addToCart}
              className="w-full rounded-xl bg-[var(--st-600)] px-4 py-3 font-semibold text-white hover:opacity-95 disabled:opacity-40"
            >
              Adicionar {readyRows.length} {readyRows.length === 1 ? 'item' : 'itens'} à sacola
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
